import { useCallback, useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api, { apiError, STATUS } from "../lib/api";
import PaxInput from "../components/PaxInput";

const fmtDate = (d) => {
  try {
    return new Date(d + "T12:00:00").toLocaleDateString("it-IT",
      { weekday: "long", day: "numeric", month: "long", year: "numeric" });
  } catch { return d; }
};

export default function PrenotazionePubblica() {
  const { token } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(false);
  const [mod, setMod] = useState({ date: "", time: "", party_size: 2 });
  const [slots, setSlots] = useState([]);
  const [saving, setSaving] = useState(false);
  const [payBusy, setPayBusy] = useState(false);

  const load = useCallback(() => {
    api.get(`/public/prenotazione/${token}`).then((r) => {
      setData(r.data);
      setMod({ date: r.data.date, time: r.data.time, party_size: r.data.party_size });
    }).catch((e) => setError(apiError(e)));
  }, [token]);

  useEffect(() => { load(); }, [load]);

  // ritorno da Stripe (carta a garanzia / caparra): lo stato si aggiorna via
  // webhook o al primo reload (fallback di lettura lato backend)
  useEffect(() => {
    const esito = searchParams.get("esito");
    if (!esito) return;
    if (esito === "carta_ok") toast.success("Carta registrata, grazie!");
    else if (esito === "pagamento_ok") toast.success("Pagamento completato, grazie!");
    else if (esito === "annullato") toast.error("Operazione annullata");
    setSearchParams({}, { replace: true });
    let n = 0;
    const t = setInterval(() => { load(); if (++n >= 5) clearInterval(t); }, 1500);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startCardSetup = async () => {
    setPayBusy(true);
    try {
      const r = await api.post(`/public/prenotazione/${token}/card-setup`);
      window.location.href = r.data.checkout_url;
    } catch (e) { toast.error(apiError(e)); setPayBusy(false); }
  };

  const startPayment = async () => {
    setPayBusy(true);
    try {
      const r = await api.post(`/public/prenotazione/${token}/pay`);
      window.location.href = r.data.checkout_url;
    } catch (e) { toast.error(apiError(e)); setPayBusy(false); }
  };

  useEffect(() => {
    if (!editing || !data) return;
    api.get(`/public/restaurant/${data.restaurant_id}/slots?date=${mod.date}`)
      .then((r) => setSlots(r.data.slots || [])).catch(() => setSlots([]));
  }, [editing, mod.date, data]);

  const doCancel = async () => {
    if (!window.confirm("Vuoi davvero cancellare la prenotazione?")) return;
    setSaving(true);
    try {
      await api.post(`/public/prenotazione/${token}/cancel`);
      toast.success("Prenotazione cancellata");
      setEditing(false);
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  const doModify = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post(`/public/prenotazione/${token}/modify`, mod);
      toast.success("Richiesta di modifica inviata — il ristorante la confermerà");
      setEditing(false);
      load();
    } catch (e2) { toast.error(apiError(e2)); }
    finally { setSaving(false); }
  };

  if (error) {
    return <div className="min-h-screen bg-zinc-100 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg border border-zinc-200 p-8 text-center text-sm text-zinc-500"
        data-testid="portal-error">{error}</div>
    </div>;
  }
  if (!data) {
    return <div className="min-h-screen bg-zinc-100 flex items-center justify-center text-sm text-zinc-400"
      data-testid="portal-loading">Caricamento...</div>;
  }

  const st = STATUS[data.status] || { label: data.status };
  const modificabile = ["da_confermare", "confermata", "lista_attesa"].includes(data.status);
  const icsUrl = `${process.env.REACT_APP_BACKEND_URL}/api/public/prenotazione/${token}/ics`;

  return (
    <div className="min-h-screen bg-zinc-100 flex items-start justify-center p-4 py-10" data-testid="portal-page">
      <div className="w-full max-w-md space-y-4">
        <div className="text-center">
          <h1 className="font-display text-2xl font-bold text-zinc-900" data-testid="portal-restaurant">{data.restaurant_name}</h1>
          {data.generali?.citta && <p className="text-xs text-zinc-500 mt-1">{data.generali.indirizzo} {data.generali.citta}</p>}
        </div>

        <div className="bg-white rounded-xl border border-zinc-200 p-6 space-y-4">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold uppercase tracking-wider text-zinc-500">La tua prenotazione</span>
            <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-zinc-900 text-white" data-testid="portal-status">
              {st.label}
            </span>
          </div>
          {data.event_title && <div className="text-sm font-semibold text-zinc-900">★ {data.event_title}</div>}
          <div className="space-y-1.5 text-sm text-zinc-700">
            <div data-testid="portal-date">📅 {fmtDate(data.date)}</div>
            <div data-testid="portal-time">🕐 {data.time}</div>
            <div data-testid="portal-party">👥 {data.party_size} {data.party_size === 1 ? "persona" : "persone"}</div>
            {data.customer_notes && <div className="text-zinc-500">Note: {data.customer_notes}</div>}
          </div>

          {data.payment_request?.status === "pagata" && (
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm font-semibold text-emerald-800"
              data-testid="portal-payment-ok">
              Caparra pagata: € {data.payment_request.amount.toFixed(2)} ✓
            </div>
          )}
          {data.payment_request?.status === "in_attesa" && !["cancellata", "rifiutata", "out", "no_show"].includes(data.status) && (
            <div className="rounded-lg border border-amber-300 bg-amber-50 p-4 space-y-2" data-testid="portal-payment-section">
              <div className="text-sm font-bold text-amber-900">Caparra richiesta: € {data.payment_request.amount.toFixed(2)}</div>
              <p className="text-xs text-amber-800">Paga ora in modo sicuro con Stripe per confermare la prenotazione.</p>
              <button onClick={startPayment} disabled={payBusy} data-testid="portal-pay-btn"
                className="w-full h-11 rounded-md text-white text-sm font-bold active:scale-95 transition disabled:opacity-50"
                style={{ backgroundColor: "#018BFA" }}>
                {payBusy ? "Attendere..." : "PAGA ORA"}
              </button>
            </div>
          )}
          {data.card && (
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm font-semibold text-emerald-800"
              data-testid="portal-card-ok">
              Carta a garanzia registrata: {data.card.brand} •••• {data.card.last4}
            </div>
          )}
          {data.card_required && !data.card && ["da_confermare", "confermata", "lista_attesa"].includes(data.status) && (
            <div className="rounded-lg border border-sky-200 bg-sky-50 p-4 space-y-2" data-testid="portal-card-section">
              <div className="text-sm font-bold text-sky-900">Carta a garanzia richiesta</div>
              <p className="text-xs text-sky-800">
                Per confermare la prenotazione registra una carta: nessun addebito ora, serve solo come garanzia.
                I dati sono gestiti in modo sicuro da Stripe.
              </p>
              <button onClick={startCardSetup} disabled={payBusy} data-testid="portal-card-setup-btn"
                className="w-full h-11 rounded-md bg-sky-600 text-white text-sm font-bold hover:bg-sky-700 active:scale-95 transition disabled:opacity-50">
                {payBusy ? "Attendere..." : "INSERISCI LA CARTA"}
              </button>
            </div>
          )}

          {data.customer_confirmed ? (
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm font-semibold text-emerald-800"
              data-testid="portal-confirmed-badge">
              Hai confermato la tua presenza ✓
            </div>
          ) : ["da_confermare", "confermata"].includes(data.status) && (
            <button onClick={async () => {
                setSaving(true);
                try {
                  await api.post(`/public/prenotazione/${token}/conferma`);
                  toast.success("Presenza confermata, grazie! A presto.");
                  load();
                } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
              }}
              disabled={saving} data-testid="portal-confirm-btn"
              className="w-full h-12 rounded-md bg-emerald-600 text-white text-sm font-bold hover:bg-emerald-700 active:scale-95 transition disabled:opacity-50">
              {saving ? "Invio..." : "CONFERMO LA MIA PRESENZA"}
            </button>
          )}

          {modificabile && !editing && (
            <div className="space-y-2 pt-2">
              {data.status === "confermata" && (
                <a href={icsUrl} data-testid="portal-calendar"
                  className="block w-full h-11 leading-[2.75rem] text-center rounded-md border border-zinc-300 text-sm font-semibold text-zinc-700 hover:bg-zinc-50">
                  Aggiungi al calendario
                </a>
              )}
              <button onClick={() => setEditing(true)} data-testid="portal-modify-btn"
                className="w-full h-11 rounded-md text-white text-sm font-semibold" style={{ backgroundColor: "#018BFA" }}>
                Modifica prenotazione
              </button>
              <button onClick={doCancel} disabled={saving} data-testid="portal-cancel-btn"
                className="w-full h-11 rounded-md border border-rose-300 text-rose-700 text-sm font-semibold hover:bg-rose-50">
                Cancella prenotazione
              </button>
            </div>
          )}

          {modificabile && editing && (
            <form onSubmit={doModify} className="space-y-3 pt-2" data-testid="portal-modify-form">
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-zinc-500 mb-1">Data</label>
                <input type="date" required value={mod.date} data-testid="portal-mod-date"
                  onChange={(e) => setMod({ ...mod, date: e.target.value, time: "" })}
                  className="w-full h-11 px-3 rounded-md border border-zinc-300 text-sm bg-white" />
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-zinc-500 mb-1">Orario</label>
                <select required value={mod.time} data-testid="portal-mod-time"
                  onChange={(e) => setMod({ ...mod, time: e.target.value })}
                  className="w-full h-11 px-3 rounded-md border border-zinc-300 text-sm bg-white">
                  <option value="">Seleziona orario</option>
                  {slots.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-zinc-500 mb-1">Persone</label>
                <PaxInput value={mod.party_size} onChange={(v) => setMod({ ...mod, party_size: v })}
                  min={1} max={50} testid="portal-mod-party" />
              </div>
              <div className="flex gap-2">
                <button type="submit" disabled={saving || !mod.time} data-testid="portal-mod-save"
                  className="flex-1 h-11 rounded-md text-white text-sm font-semibold disabled:opacity-50" style={{ backgroundColor: "#018BFA" }}>
                  {saving ? "Invio..." : "Conferma modifica"}
                </button>
                <button type="button" onClick={() => setEditing(false)} data-testid="portal-mod-cancel"
                  className="h-11 px-4 rounded-md border border-zinc-300 text-sm font-semibold text-zinc-600">
                  Annulla
                </button>
              </div>
              <p className="text-[11px] text-zinc-400">La modifica torna "da confermare": il ristorante riceverà una notifica e ti confermerà via email.</p>
            </form>
          )}

          {!modificabile && (
            <p className="text-xs text-zinc-400 pt-1" data-testid="portal-closed-note">
              Questa prenotazione non è più modificabile online. Contatta il ristorante{data.generali?.telefono ? ` al ${data.generali.telefono}` : ""}.
            </p>
          )}
        </div>
        <p className="text-center text-[11px] text-zinc-400">Powered by Dishenda</p>
      </div>
    </div>
  );
}
